/**
 * Platform → Platform settings → Verified AI apps (0322, owner 2026-10-19): the AI apps the deployment vouches for,
 * by the HOST their client metadata document is served from (claude.ai, chatgpt.com). A verified app is shown by its
 * own name, with the badge and the logo uploaded here, on the consent page and in every person's Connected apps; any
 * other is named by its host, with the unverified warning. Read with `settings:read`, changed with `settings:write`
 * (the routes in `features/orgs/api/admin-routes.ts`).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { request } from '@papan/api-client'
import { AppTile, Group, useConfirm, useToast } from '@papan/core-ui/ui'
import { Section } from './bits'

interface VerifiedApp { host: string; publisher: string | null; logoUrl: string | null; updatedAt: string; updatedBy: string | null }

const BASE = '/api/admin/mcp-verified-apps'

export function VerifiedAppsSetting({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const confirm = useConfirm()
  const [apps, setApps] = useState<VerifiedApp[] | null>(null)
  const [host, setHost] = useState('')
  const [publisher, setPublisher] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const fileFor = useRef<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const k = (key: string, o?: Record<string, unknown>) => t(`settings.platform.settings.verifiedApps.${key}`, o)

  const load = useCallback(() => {
    request<{ apps: VerifiedApp[] }>(BASE).then(r => setApps(r.apps)).catch(() => setApps([]))
  }, [])
  useEffect(() => { load() }, [load])

  const run = async (key: string, work: () => Promise<unknown>, done: string) => {
    setBusy(key)
    try {
      await work()
      toast.success(done)
      load()
    } catch (e) {
      toast.error((e as Error)?.message || k('failed'))
    } finally {
      setBusy(null)
    }
  }

  const add = () => run('add', async () => {
    await request(`${BASE}/${encodeURIComponent(host.trim())}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ publisher: publisher.trim() || null }),
    })
    setHost('')
    setPublisher('')
  }, k('added'))

  const remove = async (app: VerifiedApp) => {
    const ok = await confirm({
      title: k('removeTitle', { host: app.host }),
      message: k('removeConfirm'),
      confirmText: k('remove'),
      type: 'danger',
    })
    if (ok) await run(`rm:${app.host}`, () => request(`${BASE}/${encodeURIComponent(app.host)}`, { method: 'DELETE' }), k('removed'))
  }

  const pickLogo = (hostName: string) => {
    fileFor.current = hostName
    fileInput.current?.click()
  }

  const uploadLogo = async (file: File | undefined) => {
    const target = fileFor.current
    if (!file || !target) return
    if (fileInput.current) fileInput.current.value = ''
    await run(`logo:${target}`, () => request(`${BASE}/${encodeURIComponent(target)}/logo`, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    }), k('logoSaved'))
  }

  const clearLogo = (app: VerifiedApp) => run(`logo:${app.host}`, () => request(`${BASE}/${encodeURIComponent(app.host)}/logo`, { method: 'DELETE' }), k('logoCleared'))

  return (
    <Section title={k('title')} desc={k('desc')}>
      <div className="max-w-2xl space-y-3" data-testid="verified-apps">
        <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
        {apps === null
          ? <p className="text-xs themed-text-muted">{t('common:loading')}</p>
          : (
            <Group footer={k('footer')}>
              {apps.length === 0 && <p className="px-4 py-3 text-sm themed-text-muted">{k('none')}</p>}
              {apps.map(app => (
                <div key={app.host} className="flex items-center gap-3 px-4 py-3" data-testid={`verified-app-${app.host}`}>
                  <AppTile name={app.publisher ?? app.host} host={app.host} logoUrl={app.logoUrl} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium themed-text">{app.host}</span>
                    <span className="block truncate text-xs themed-text-muted">{app.publisher ?? k('noPublisher')}</span>
                  </span>
                  {canEdit && (
                    <span className="flex shrink-0 items-center gap-1">
                      <button type="button" disabled={!!busy} onClick={() => pickLogo(app.host)}
                        className="rounded-md px-2 py-1 text-xs text-blue-400 hover:bg-blue-500/10 disabled:opacity-50" data-testid={`verified-app-logo-${app.host}`}>
                        {app.logoUrl ? k('replaceLogo') : k('uploadLogo')}
                      </button>
                      {app.logoUrl && (
                        <button type="button" disabled={!!busy} onClick={() => clearLogo(app)}
                          className="rounded-md px-2 py-1 text-xs themed-text-muted themed-bg-hover disabled:opacity-50">
                          {k('clearLogo')}
                        </button>
                      )}
                      <button type="button" disabled={!!busy} onClick={() => remove(app)}
                        className="rounded-md px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 disabled:opacity-50" data-testid={`verified-app-remove-${app.host}`}>
                        {k('remove')}
                      </button>
                    </span>
                  )}
                </div>
              ))}
              {canEdit && (
                <form
                  className="flex flex-wrap items-center gap-2 px-4 py-3"
                  onSubmit={(e) => { e.preventDefault(); if (host.trim()) void add() }}
                >
                  <input value={host} onChange={(e) => setHost(e.target.value)} placeholder={k('hostPlaceholder')} aria-label={k('host')}
                    className="themed-input min-w-0 flex-1 rounded-md border px-2 py-1.5 text-sm" data-testid="verified-app-host" />
                  <input value={publisher} onChange={(e) => setPublisher(e.target.value)} placeholder={k('publisherPlaceholder')} aria-label={k('publisher')}
                    className="themed-input min-w-0 flex-1 rounded-md border px-2 py-1.5 text-sm" />
                  <button type="submit" disabled={!host.trim() || !!busy}
                    className="rounded-md px-3 py-1.5 text-sm font-medium themed-btn-primary disabled:opacity-50" data-testid="verified-app-add">
                    {k('add')}
                  </button>
                </form>
              )}
            </Group>
          )}
      </div>
    </Section>
  )
}
